import gsap from 'gsap';

export default class FiltersAccordion extends HTMLElement {
  private button: HTMLButtonElement | null = null;
  private container: HTMLDivElement | null = null;
  private content: HTMLDivElement | null = null;
  private filters: HTMLInputElement[] | null = null;
  private iconShow: HTMLSpanElement | null = null;
  private iconHide: HTMLSpanElement | null = null;

  private isOpen: boolean = false;

  private contentHeight: number = 0;

  private openTl: gsap.core.Timeline | null = null;
  private closeTl: gsap.core.Timeline | null = null;

  private onResizeBound = this.onResize.bind(this);
  private onButtonClickBound = this.onButtonClick.bind(this);

  static observedAttributes = ['data-is-open'];

  constructor() {
    super();
  }

  connectedCallback(): void {
    this.isOpen = this.dataset.isOpen === 'true';

    this.button = this.querySelector('[data-filters-accordion-ref="button"]');
    this.container = this.querySelector('[data-filters-accordion-ref="container"]');
    this.content = this.querySelector('[data-filters-accordion-ref="content"]');
    this.iconShow = this.querySelector('[data-filters-accordion-ref="icon-show"]');
    this.iconHide = this.querySelector('[data-filters-accordion-ref="icon-hide"]');
    this.filters = Array.from(this.querySelectorAll<HTMLInputElement>('[data-filters-accordion-ref="filters"]'));
    this.addEventListeners();
  }

  addEventListeners(): void {
    window.addEventListener('resize', this.onResizeBound);
    this.onResize();

    if (this.button) {
      this.button.addEventListener('click', this.onButtonClickBound);
    }
  }

  removeEventListeners(): void {
    window.removeEventListener('resize', this.onResizeBound);

    if (this.button) {
      this.button.removeEventListener('click', this.onButtonClickBound);
    }
  }

  onButtonClick(): void {
    this.dataset.isOpen = this.isOpen ? 'false' : 'true';
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (name === 'data-is-open' && oldValue !== newValue) {
      this.isOpen = newValue === 'true';
      if (this.isOpen) {
        this.open();
      } else {
        this.close();
      }
    }
  }

  open() {
    this.isOpen = true;

    if (this.closeTl) {
      this.closeTl.kill();
    }
    if (this.openTl) {
      this.openTl.kill();
    }

    this.openTl = gsap.timeline();
    this.openTl.to(this.container, { height: this.contentHeight, duration: 0.6, ease: 'power3.out' }, 0);
    this.openTl.fromTo(
      this.filters,
      {
        y: 10,
        transformOrigin: 'bottom',
      },
      {
        y: 0,
        duration: 0.6,
        stagger: { amount: 0.15 },
        ease: 'expo.out',
      },
      0,
    );
    this.openTl.fromTo(
      this.filters,
      {
        autoAlpha: 0,
      },
      {
        autoAlpha: 1,
        duration: 0.6,
        stagger: { amount: 0.15 },
        ease: 'power2.out',
      },
      0,
    );
    this.openTl.set(this.iconShow, { display: 'none' }, 0);
    this.openTl.set(this.iconHide, { display: 'block' }, 0);
  }

  close() {
    this.isOpen = false;

    if (this.openTl) {
      this.openTl.kill();
    }
    if (this.closeTl) {
      this.closeTl.kill();
    }
    this.closeTl = gsap.timeline();
    this.closeTl.to(this.container, { height: 0, duration: 0.6, ease: 'expo.out' }, 0);
    this.closeTl.to(
      this.filters,
      {
        autoAlpha: 0,
        duration: 0.3,
        ease: 'power2.out',
      },
      0,
    );
    this.closeTl.set(this.iconShow, { display: 'block' }, 0);
    this.closeTl.set(this.iconHide, { display: 'none' }, 0);
  }

  onResize(): void {
    if (this.content) {
      this.contentHeight = this.content.getBoundingClientRect().height;
    }
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
    if (this.openTl) {
      this.openTl.kill();
    }
    if (this.closeTl) {
      this.closeTl.kill();
    }
  }
}

customElements.define('filters-accordion', FiltersAccordion);
